import { z } from 'zod';

export enum LandingCardActionTypeEnum {
  Dispatch = 1,
  PromptInjection = 2,
  Custom = 3,
  Redirect = 4,
  PassResponseDirectlyToAgent = 5,
  ChatAction = 6,
  HcpSearch = 7,
}

export const LandingCardActionTypeSchema = z.enum(LandingCardActionTypeEnum);
export type LandingCardActionType = z.infer<typeof LandingCardActionTypeSchema>;

export const dispatchActionDataSchema = z.object({});

export const promptInjectionActionDataSchema = z.object({
  prompt: z.string(),
});

export const redirectActionDataSchema = z.object({
  url: z.string(),
});

export const chatActionActionDataSchema = z.discriminatedUnion('chatActionType', [
  z.object({
    chatActionType: z.literal('InitiateFormWorkflow'),
    workflowIdentifier: z.uuid('Workflow is required'),
    prompt: z.string().min(1, 'Prompt is required'),
  }),
  z.object({
    chatActionType: z.literal('StartFrmAccountSearch'),
    prompt: z.string().min(1, 'Prompt is required'),
  }),
]);

export type ChatActionActionData = z.infer<typeof chatActionActionDataSchema>;

export const hcpSearchActionDataSchema = z.object({});

// Validation schema for the jsonData content when using PassResponseDirectlyToAgent -
// this will need to be changed if we want anything other than subject configured to be passed to agent in landing card
export const agentResponseJsonDataSchema = z.object({
  subject: z.object({
    name: z.string().min(1, 'Name is required'),
    id: z
      .string()
      .min(1, 'ID is required')
      .refine((val) => !isNaN(Number(val)), {
        error: 'ID must be a valid number',
      }),
  }),
});

export type AgentResponseJsonData = z.infer<typeof agentResponseJsonDataSchema>;

export const landingCardActionDataSchema = z.union([
  dispatchActionDataSchema,
  promptInjectionActionDataSchema,
  redirectActionDataSchema,
  agentResponseJsonDataSchema,
  chatActionActionDataSchema,
  hcpSearchActionDataSchema,
  z.null(),
]);

export type LandingCardActionData = z.infer<typeof landingCardActionDataSchema>;

export const accountLandingCardOverrideDataSchema = z
  .object({
    defaultCardId: z.number().nullable(),
  })
  .nullable();

/**
 * What an application landing card points at for the fields it does not set itself: a built-in default
 * card, or (for a brand's own card only) one of the brand's account cards.
 */
export const applicationLandingCardOverrideDataSchema = z
  .union([
    z.object({
      defaultCardId: z.number(),
    }),
    z.object({
      accountCardId: z.number(),
    }),
  ])
  .nullable();

export const landingCardOverrideDataSchema = z.union([
  accountLandingCardOverrideDataSchema,
  applicationLandingCardOverrideDataSchema,
  z.null(),
]);

export type AccountLandingCardOverrideData = z.infer<typeof accountLandingCardOverrideDataSchema>;
export type ApplicationLandingCardOverrideData = z.infer<typeof applicationLandingCardOverrideDataSchema>;
export type LandingCardOverrideData = z.infer<typeof landingCardOverrideDataSchema>;

export const getActionTypeLabel = (actionType: LandingCardActionTypeEnum) => {
  switch (actionType) {
    case LandingCardActionTypeEnum.Dispatch:
      return 'Dispatch';
    case LandingCardActionTypeEnum.PromptInjection:
      return 'Prompt Injection';
    case LandingCardActionTypeEnum.Custom:
      return 'Custom';
    case LandingCardActionTypeEnum.Redirect:
      return 'Redirect';
    case LandingCardActionTypeEnum.PassResponseDirectlyToAgent:
      return 'Pass response directly to agent';
    case LandingCardActionTypeEnum.ChatAction:
      return 'Chat Action';
    case LandingCardActionTypeEnum.HcpSearch:
      return 'HCP Search';
    default:
      return 'Unknown';
  }
};
